import { useQuery } from '@tanstack/react-query';
import { publicApiClient } from '@/api/client';

/**
 * Shape returned by GET /api/customer-portal-status.
 * The backend always returns this regardless of the kill-switch state
 * because the endpoint sits outside the portal middleware.
 */
interface PortalStatusResponse {
  enabled: boolean;
}

/**
 * Fetch the public portal status endpoint via publicApiClient.
 *
 * Why publicApiClient (not the default apiClient): the standard apiClient has a
 * 401-redirect interceptor that would push the user to /portal/login on auth
 * errors. This hook is what the maintenance gate polls, so an interceptor-driven
 * redirect here would loop. publicApiClient is the explicit opt-out for
 * unauthenticated public endpoints.
 */
async function fetchPortalStatus(): Promise<PortalStatusResponse> {
  try {
    const response = await publicApiClient.get<PortalStatusResponse>(
      '/api/customer-portal-status'
    );
    return response.data;
  } catch {
    // Fail-open: transient outages must not lock customers out prematurely.
    // A genuine "disabled" state is a 200 with {enabled:false}, so any error
    // here (network failure, 503, etc.) is by definition not the disabled signal.
    return { enabled: true };
  }
}

/**
 * React Query hook that polls the public portal status endpoint every 60 s
 * when the tab is visible. Fail-open: errors resolve to `{enabled: true}`.
 */
export function usePortalStatus(): { enabled: boolean; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['public', 'customer-portal-status'],
    queryFn: fetchPortalStatus,
    staleTime: 60_000,
    refetchInterval: (query) => {
      // Only poll when the document is visible
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return false;
      }
      return query.state.data !== undefined ? 60_000 : false;
    },
    refetchOnWindowFocus: true,
    // Never throw — treat all errors as enabled:true (fail open)
    throwOnError: false,
    retry: false,
  });

  return {
    enabled: data?.enabled ?? true,
    isLoading,
  };
}
